import {
	InspectionResolvedResponse,
	updateInspectionData,
	UpdateInspectionParams,
} from '@Types/inspectionAttributes.types';
import { put, retry, takeLatest, call } from 'redux-saga/effects';
import {
	createInspection,
	getInspectionAttributes,
	getInspectionAttributesRejected,
	getInspectionAttributesResolved,
	refreshInspection,
	setCreateInspectionId,
	setCreatingInspection,
	updateInspection,
} from '@Actions/inspection.actions';
import { AnyAction } from 'redux';
import { AxiosError, AxiosResponse } from 'axios';
import {
	createInspectionApi,
	getInspectionApi,
	getUpdateInspectionApi,
} from '@Services';
import { error, success } from '@innovyze/stylovyze';
import {
	getUpdateFailureMessage,
	getUpdateSuccessMessage,
} from '@Utils/updateSuccessMessage.utils';
import { t } from 'i18next';
import {
	CreateInspectionParams,
	CreateInspectionResponse,
} from '@Types/inspectionAttributes.types';
import { PayloadAction } from '@reduxjs/toolkit';

function* getInspectionsSaga(action: AnyAction) {
	try {
		const payload: AxiosResponse<InspectionResolvedResponse> = yield retry(
			5,
			15000,
			getInspectionApi,
			action.payload.assetId,
		);
		yield put(getInspectionAttributesResolved(payload.data));
	} catch (_e) {
		yield put(getInspectionAttributesRejected());
	}
}

function* watchGetInspections() {
	yield takeLatest(getInspectionAttributes, getInspectionsSaga);
}

/**
 * Returns `updateStatus` to the reducer's initial value.
 *
 * The properties components set `updateStatus` to -1 immediately before
 * dispatching an update, and render every edit control as
 * `disabled={inspUpdatestatus === -1}`. Only this saga ever clears it, and
 * before this it did so on HTTP 200 alone - so a failed save left every card on
 * the page disabled, with no way to retry, until a full page reload. Redux state
 * survives navigation, so leaving the page and returning did not help.
 *
 * 0 rather than the response status: -1 is the disabled state itself, and 200
 * triggers the components' onReRender, which a failed save has not earned. Note
 * `rejection.code` is *not* safe here - it falls back to -1 when there is no
 * response, which is exactly what a timeout after the retries produces.
 */
const UPDATE_STATUS_IDLE = { status: 0 };

// Exported for tests: the stuck-controls bug above is control flow in this
// generator, not something a pure helper can express.
export function* updateInspectionsSaga(action: AnyAction) {
	// Narrowed once, so the toast's use of `recordType` is checked against
	// UpdateInspectionParams rather than read off an untyped payload - removing
	// or renaming the field becomes a compile error here instead of silently
	// reverting every task and event toast to saying "Inspection".
	const params: UpdateInspectionParams = action.payload;
	try {
		const payload: AxiosResponse<updateInspectionData> = yield retry(
			5,
			15000,
			getUpdateInspectionApi,
			params,
		);
		if (payload?.status === 200) {
			yield put(success(getUpdateSuccessMessage(params?.recordType)));
			yield put(refreshInspection(payload));
		} else {
			yield put(refreshInspection(UPDATE_STATUS_IDLE));
		}
	} catch (e) {
		const err = e as AxiosError;
		const rejection = {
			code: err?.response?.status ?? -1,
			text:
				err?.message ??
				err?.response?.statusText ??
				t('No additional information'),
		};
		yield put(
			error(getUpdateFailureMessage(rejection.text, params?.recordType)),
		);
		yield put(refreshInspection(UPDATE_STATUS_IDLE));
	}
}

function* watchUpdateInspections() {
	yield takeLatest(updateInspection, updateInspectionsSaga);
}

function* createInspectionSaga(action: PayloadAction<CreateInspectionParams>) {
	try {
		yield put(setCreatingInspection(true));

		const payload: AxiosResponse<CreateInspectionResponse> = yield call(
			createInspectionApi,
			action.payload,
		);

		yield put(setCreateInspectionId(payload.data._id));
		yield put(setCreatingInspection(false));
	} catch (e) {
		console.log(e);
		const err = e as AxiosError;
		const rejection = {
			code: err?.response?.status ?? -1,
			text:
				err?.message ??
				err?.response?.statusText ??
				t('No additional information'),
		};

		yield put(setCreateInspectionId(undefined));
		yield put(setCreatingInspection(false));
		yield put(
			error(
				t('New {{type}} Failed: {{reason}}', {
					type: action.payload.options.task
						? t('Inspection')
						: t('Task'),
					reason: rejection.text,
				}),
			),
		);
	}
}

function* watchCreateInspection() {
	yield takeLatest(createInspection, createInspectionSaga);
}

export default [
	watchGetInspections(),
	watchUpdateInspections(),
	watchCreateInspection(),
];
